// A process-wide cache of the /mcc section's three heavy fetches -- the manufacturing-line listing, each
// manufacturing line's best score, and the picked plan's solve -- so a fresh ManufacturingLineLists instance
// reuses what an identical fetch just produced instead of re-running the work. This is what stops the section
// tab from re-listing, re-scoring and re-solving everything the guided setup wizard just did the moment the
// wizard closes: the wizard and the tab are SEPARATE ManufacturingLineLists instances, so the tab mounting
// fresh would otherwise fire all three fetches again and double the user's wait.
//
// Keyed by the request URL alone, because the URL now carries EVERY input the response varies on:
//   - The section index, the filter checkboxes (Bobbin as bobbin_enabled, and the printing- / die-cutting-machine
//     ticks as disabled_filters[] plus the *_type[] params), the area-ratio cap, and -- for the solve and best
//     score -- the picked plan.
//   - The quantity (count). The client sends count as a URL param on all three fetches, and the server scores /
//     solves against that param (falling back to the quote's own count only when it is absent). So client and
//     server always agree on the quantity, AND count is in the key for free -- a quantity change mints a
//     different URL and misses. (Sending count also fixes a race: the count field persists user_quote.count
//     asynchronously, so a server that read the quote could score against a not-yet-saved quantity.)
// What the URL cannot carry is the state the answer was computed AGAINST but the viewer never chose: the
// quote's configuration, the dieline being priced, and the machine catalogue the score is worked out from.
// That is the FINGERPRINT's job (Frank::ResponseCacheFingerprint, rendered as window.frank_response_cache_
// fingerprint) -- see ManufacturingLineResponseStore, where it decides what may be read back.
//
// The Map itself is unchanged: a plain module-level Map, read synchronously, that lives for the page session.
// It is now HYDRATED from IndexedDB before the page mounts and written through to it afterwards, so the same
// answers survive a refresh instead of being recomputed from nothing -- every one of those best scores is a
// full solve server-side, and a section has dozens.

import {
  readPersistedResponses, persistResponse, clearPersistedResponses, sweepPersistedResponses
} from 'components/frank/utils/ManufacturingLineResponseStore.js';
import {
  fetchSectionAnswer, SectionAnswerUnavailableError
} from 'components/frank/utils/sectionAnswerPolling.js';

const responseCache = new Map();

// The requests that have GONE OUT and not come back, keyed exactly as the cache above. The cache alone cannot
// stop repeated work, because a URL only enters it once its answer has LANDED -- so two asks for the same thing
// while the first is still in flight both went to the server. That is not hypothetical for the solve, which
// frank.contract calls the expensive half of the page: picking a manufacturing line starts it, and the picked
// card's own "Next" button moves the user to the Solution stage seconds later, which re-runs the solve effect
// (the stage is one of its dependencies) with the very same URL. Measured before this map existed: two identical
// /mcc/<quote>/solutions/<index> requests for one pick.
//
// A second ask AWAITS THE FIRST instead of repeating it. The first caller's own request is superseded by then and
// throws its answer away, so the shared promise is what the surviving caller reads.
//
// The joiner takes the FIRST caller's `options`, since there is only one request: no caller passes any today
// (both are plain GETs), and one that wants its own headers or abort signal honoured must not be quietly joined
// to someone else's -- give it its own path rather than adding options to this key.
const inFlightRequests = new Map();

// What the persisted half is keyed by for THIS page: the server's fingerprint and the quote on screen. Null
// until hydrateResponseCache runs, and left null on a page that renders no fingerprint -- which is what keeps
// every other surface (and the JS tests) on exactly the memory-only behaviour they had before.
let persistence = null;

// The canonical key: the URL with its query parameters sorted, so an identical request keys the same regardless
// of the order the params happened to be appended in. Sorting is a lossless canonicalisation (two different
// input sets can never collapse to one key), so a cache HIT always means a truly identical request; a differing
// param order can at worst cause a harmless MISS, never a wrong hit.
const canonicalUrl = (url) => {
  const [path, query = ''] = url.split('?');
  if (!query) { return path; }
  const params = new URLSearchParams(query);
  params.sort();
  return `${path}?${params.toString()}`;
};

export const hasCachedResponse = (url) => responseCache.has(canonicalUrl(url));
export const getCachedResponse = (url) => responseCache.get(canonicalUrl(url));
// Returns when the write-through below has landed. No caller on the page waits for it -- the Map above is what
// this page reads, and the store is only for the NEXT one -- but a test that wants to reopen the store has to
// know the write is finished.
export const setCachedResponse = (url, data) => {
  responseCache.set(canonicalUrl(url), data);
  if (!persistence) { return Promise.resolve(); }

  return persistResponse({ ...persistence, url: canonicalUrl(url), data });
};
export const clearResponseCache = () => {
  responseCache.clear();
  // The in-flight map goes too, or a request started before the clear stays joinable afterwards -- a later
  // caller would await an answer computed for the state that was just thrown away.
  inFlightRequests.clear();
  if (persistence) { clearPersistedResponses(persistence); }
};

// READ THE PERSISTED ANSWERS BACK INTO THE MAP, once, before the /mcc page mounts (FrankQuoteBuilderPage gates
// its render on this). Doing it up front rather than inside the fetch helpers is what lets every reader stay
// SYNCHRONOUS -- the best-score effect asks hasCachedResponse(url) mid-render and cannot await anything -- and
// keeps all knowledge of persistence in these two files.
//
// A page with no fingerprint (any surface that is not /mcc edit, and the JS tests) resolves immediately and
// persists nothing, so the memory-only behaviour is exactly what it always was.
export const hydrateResponseCache = ({ fingerprint, variant, userId, quoteId }) => {
  if (!fingerprint) { return Promise.resolve(0); }
  // The /mcc page renders the user beside the fingerprint, so a fingerprint without one is a BROKEN page, not a
  // surface that does without persistence. Say so rather than quietly storing rows that belong to nobody --
  // they would be unreadable next load and undeletable by the per-user sweep.
  if (!userId) {
    if (typeof trackEvent === 'function') {
      trackEvent('js_error_happened', {
        exception: '[ManufacturingLineResponseCache#hydrateResponseCache] fingerprint rendered with no user',
        user_quote_id: quoteId
      });
    }
    return Promise.resolve(0);
  }

  persistence = { fingerprint, variant, userId, quoteId };
  return readPersistedResponses(persistence).then((entries) => {
    // Anything already fetched by this page wins: it is the newer answer to the same question.
    entries.forEach((entry) => {
      if (!responseCache.has(entry.url)) { responseCache.set(entry.url, entry.data); }
    });
    // Housekeeping for future page loads (another user's rows, expired ones, then the ceiling), so it runs
    // AFTER hydration and is not waited on.
    sweepPersistedResponses(persistence);
    return entries.length;
  });
};

// Fetch JSON through the cache: a cached response resolves immediately (no network, no request-queue slot); an
// identical request already IN FLIGHT is joined rather than repeated (see inFlightRequests above); otherwise
// ASK UNTIL ANSWERED, store on success, and return it.
//
// Asking until answered is what these endpoints now need: they compute on the manufacturing_cost_calculator
// queue and answer `{ status: 'processing' }` in the meantime (see sectionAnswerPolling). It happens in HERE
// rather than at the three call sites because the URL is unchanged and so is the promise they already await --
// the listing, the solve and the best score go on asking for exactly what they asked for before, and the
// in-flight map means a second asker joins the first one's poll loop instead of starting a second one.
//
// The contract each caller was written against is kept exactly: null when the server has no answer for us (a
// non-OK response, or an answer the server reports as failed) -- NOT cached, so a transient failure does not
// stick -- and a rejection for anything else (a network or parse error), which the callers own and each wraps
// in its own .catch. A rejection reaches every JOINED caller too, which is right: they asked for the one
// request that failed. The abort signal is honoured for a live poll; a cache hit has nothing to abort.
//
// `onUnavailable` is how a caller learns WHY there is no answer, without that null becoming a rejection for
// everybody. The server says why it could not answer -- in development, that nothing is listening on the
// manufacturing_cost_calculator queue and how to start one -- and a caller that draws the failure needs to be
// able to repeat it, rather than leaving the reason in a console nobody has open. Optional, so the callers
// that only need "no answer" (the solve) are unchanged.
//   A JOINED caller does not get it: there is one request, and the reason goes to whoever asked first. Both
//   still see the null, so both know there is no answer -- only the wording of it is the first asker's.
export const fetchJsonCached = (url, { onUnavailable, ...options } = {}) => {
  if (hasCachedResponse(url)) {
    return Promise.resolve(getCachedResponse(url));
  }

  const key = canonicalUrl(url);
  if (inFlightRequests.has(key)) { return inFlightRequests.get(key); }

  const request = fetchSectionAnswer(url, options)
    .then((data) => {
      if (data !== null) { setCachedResponse(url, data); }
      return data;
    })
    .catch((error) => {
      // Null is what every caller here was written against, so an unavailable answer still resolves to it --
      // but the REASON is not thrown away on the way. The server says why it could not answer (a failed
      // computation carries its message), and converting that straight to null discarded it before any
      // caller's own .catch could report it.
      if (error instanceof SectionAnswerUnavailableError) {
        console.error(`No answer for ${url}:`, error.message);
        if (onUnavailable) { onUnavailable(error); }
        return null;
      }
      throw error;
    })
    // Settled either way -- a failed request must leave nothing joinable behind, or the next attempt would
    // inherit this one's rejection instead of asking the server again.
    .finally(() => { inFlightRequests.delete(key); });

  inFlightRequests.set(key, request);
  return request;
};
